import { Controller } from "@hotwired/stimulus"

// Media Session API controller for background playback and lock screen controls.
// Connects to data-controller="audio-player"
export default class extends Controller {
  static values = {
    itemTitle: String,
    spaceName: { type: String, default: "" },
    artworkUrl: { type: String, default: "" },
    deferMediaSession: { type: Boolean, default: false },
  }

  declare itemTitleValue: string
  declare spaceNameValue: string
  declare artworkUrlValue: string
  declare deferMediaSessionValue: boolean

  private mediaElement: HTMLAudioElement | HTMLElement | null = null
  private playHandler: (() => void) | null = null
  private pauseHandler: (() => void) | null = null
  private endedHandler: (() => void) | null = null
  // Whether this player currently owns the Media Session. Only an owner
  // follows a metadata change — a deferred player (one of many on a container
  // page) must not seize the lock screen, and Stimulus fires the value
  // callbacks once before connect(), when no session exists yet.
  private sessionArmed = false

  connect() {
    this.findMediaElement()
    if (!this.deferMediaSessionValue) {
      this.setupMediaSession()
    }
    this.setupMediaElementListeners()
  }

  // Called by container-player when this track becomes the active one.
  activateMediaSession() {
    this.setupMediaSession()
  }

  // The unfurl player (#1643) re-points this host at the selected track. The
  // lock screen has to follow, or it keeps announcing the track the page was
  // rendered for while a different one plays.
  itemTitleValueChanged() {
    this.refreshMediaSession()
  }

  artworkUrlValueChanged() {
    this.refreshMediaSession()
  }

  // The unfurl player (#1643) swaps the transport element in place when the
  // next track needs a different one, which detaches the element cached at
  // connect — Media Session would keep driving a node no longer in the DOM.
  // Wired from shared/_audio_player as embed-player:mediaswapped.
  refreshMediaElement() {
    this.removeMediaElementListeners()
    this.findMediaElement()
    this.setupMediaElementListeners()
    this.refreshMediaSession()
  }

  private refreshMediaSession() {
    if (!this.sessionArmed) return
    this.setupMediaSession()
  }

  disconnect() {
    this.removeMediaElementListeners()
    this.cleanupMediaSession()
  }

  private findMediaElement() {
    const muxAudio = this.element.querySelector("mux-audio")
    const audioElement = this.element.querySelector("audio")
    this.mediaElement = (muxAudio ?? audioElement) as
      HTMLAudioElement | HTMLElement | null
  }

  private setupMediaElementListeners() {
    if (!this.mediaElement) return

    this.playHandler = () => {
      this.updatePlaybackState("playing")
    }

    this.pauseHandler = () => {
      this.updatePlaybackState("paused")
    }

    this.endedHandler = () => {
      this.updatePlaybackState("none")
    }

    this.mediaElement.addEventListener("play", this.playHandler)
    this.mediaElement.addEventListener("pause", this.pauseHandler)
    this.mediaElement.addEventListener("ended", this.endedHandler)
  }

  private removeMediaElementListeners() {
    if (this.mediaElement && this.playHandler) {
      this.mediaElement.removeEventListener("play", this.playHandler)
    }
    if (this.mediaElement && this.pauseHandler) {
      this.mediaElement.removeEventListener("pause", this.pauseHandler)
    }
    if (this.mediaElement && this.endedHandler) {
      this.mediaElement.removeEventListener("ended", this.endedHandler)
    }
    this.playHandler = null
    this.pauseHandler = null
    this.endedHandler = null
  }

  private setupMediaSession() {
    if (!("mediaSession" in navigator)) return

    const mediaSession = navigator.mediaSession

    const metadata: MediaMetadataInit = {
      title: this.itemTitleValue,
      artist: this.spaceNameValue || "Indee",
    }

    if (this.artworkUrlValue) {
      metadata.artwork = [{ src: this.artworkUrlValue }]
    }

    mediaSession.metadata = new MediaMetadata(metadata)
    this.sessionArmed = true

    mediaSession.setActionHandler("play", () => {
      if (this.mediaElement && "play" in this.mediaElement) {
        void this.mediaElement.play()
      }
    })

    mediaSession.setActionHandler("pause", () => {
      if (this.mediaElement && "pause" in this.mediaElement) {
        this.mediaElement.pause()
      }
    })

    try {
      mediaSession.setActionHandler("seekbackward", () => {
        if (this.mediaElement && "currentTime" in this.mediaElement) {
          this.mediaElement.currentTime = Math.max(
            this.mediaElement.currentTime - 10,
            0,
          )
        }
      })
    } catch {
      // seekbackward not supported in this browser
    }

    try {
      mediaSession.setActionHandler("seekforward", () => {
        if (this.mediaElement && "currentTime" in this.mediaElement) {
          this.mediaElement.currentTime = Math.min(
            this.mediaElement.currentTime + 30,
            this.mediaElement.duration || Infinity,
          )
        }
      })
    } catch {
      // seekforward not supported in this browser
    }
  }

  private updatePlaybackState(state: "playing" | "paused" | "none") {
    if ("mediaSession" in navigator) {
      navigator.mediaSession.playbackState = state
    }
  }

  private cleanupMediaSession() {
    this.sessionArmed = false
    if (!("mediaSession" in navigator)) return

    navigator.mediaSession.metadata = null
    navigator.mediaSession.setActionHandler("play", null)
    navigator.mediaSession.setActionHandler("pause", null)
    try {
      navigator.mediaSession.setActionHandler("seekbackward", null)
    } catch {
      // seekbackward not supported
    }
    try {
      navigator.mediaSession.setActionHandler("seekforward", null)
    } catch {
      // seekforward not supported
    }
  }
}
